'use client';

/**
 * OrgBillingProfile — the composite per-org billing package in ONE view.
 * Select an organization → see its complete pricing: assigned plan tier (with
 * per-client overrides applied), transaction fees, and per-org consumption/AI
 * rates. Read-only summary; each dimension is edited in the sections below
 * (Plan Tiers, Assign, Transaction Fees) — this is the single source of truth
 * for "what does this client actually pay across all models".
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Building2, Package, Percent, MessageSquare, Bot } from 'lucide-react';

const PLATFORM_BACKEND_URL =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_PLATFORM_API_URL) ||
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_WEAVEIN_API_URL) ||
  'https://weavein-api.onrender.com';

function getAuthHeaders(): Record<string, string> {
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
  const cboId = typeof window !== 'undefined' ? localStorage.getItem('active_cbo_id') : null;
  const tenantSlug = typeof window !== 'undefined' ? localStorage.getItem('platform_tenant_slug') : null;
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) h['Authorization'] = `Bearer ${token}`;
  if (cboId) h['X-CBO-ID'] = cboId;
  if (tenantSlug) h['X-Tenant-ID'] = tenantSlug;
  return h;
}
const revGet = async <T = any>(p: string): Promise<T | null> => {
  try {
    const r = await fetch(`${PLATFORM_BACKEND_URL}/api/platform/v1/revops${p}`, { headers: getAuthHeaders() });
    return r.ok ? await r.json() : null;
  } catch { return null; }
};

interface Org { id: number; name: string }
const modelLabel: Record<string, string> = {
  flat_fee: 'Flat fee', per_seat: 'Per user / mo (PUPM)', pmpm: 'Per member / mo (PMPM)',
  tiered: 'Tiered', revenue_share: 'Reimbursement / rev share', hybrid: 'Hybrid',
};
const money = (n: any) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 4 })}`;

function Val({ label, value, custom }: { label: string; value: string; custom?: boolean }) {
  return (
    <div className="flex items-center justify-between py-1 text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-gray-900">
        {value}
        {custom && <span className="ml-2 rounded bg-indigo-50 px-1.5 py-0.5 text-xs text-indigo-700">custom</span>}
      </span>
    </div>
  );
}

export default function OrgBillingProfile() {
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [cboId, setCboId] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    fetch(`${PLATFORM_BACKEND_URL}/api/cbos`, { headers: getAuthHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setOrgs((Array.isArray(d) ? d : d.cbos || []).map((o: any) => ({ id: o.id, name: o.name }))))
      .catch(() => setOrgs([]));
  }, []);

  const load = useCallback(async (cbo: number) => {
    setLoading(true); setData(null);
    const [tiersRes, assignRes, feesRes, commRes, aiRes] = await Promise.all([
      revGet<{ shared_tiers: any[] }>('/shared-tiers'),
      revGet<{ assignments: any[] }>(`/tier-assignments?cbo_id=${cbo}`),
      revGet<{ fee_overrides: any[] }>(`/fee-overrides?cbo_id=${cbo}`),
      revGet<{ communication_pricing: any[] }>(`/communication-pricing?target_organization_id=${cbo}`),
      revGet<{ ai_token_pricing: any[] }>(`/ai-token-pricing?target_organization_id=${cbo}`),
    ]);
    const templates = tiersRes?.shared_tiers || [];
    const assignment = (assignRes?.assignments || []).find((a: any) => a.status === 'active') || null;
    const template = assignment ? templates.find((t: any) => t.id === assignment.tier_template_id) : null;
    setData({
      assignment,
      template,
      fees: feesRes?.fee_overrides || [],
      comm: (commRes?.communication_pricing || []).filter((r: any) => r.target_organization_id === cbo),
      ai: (aiRes?.ai_token_pricing || []).filter((r: any) => r.target_organization_id === cbo),
    });
    setLoading(false);
  }, []);

  const eff = useMemo(() => {
    if (!data?.assignment) return null;
    const a = data.assignment, t = data.template || {};
    const pick = (ovr: any, base: any) => (ovr !== null && ovr !== undefined ? { v: ovr, custom: true } : { v: base ?? 0, custom: false });
    return {
      name: a.tier_name,
      model: a.pricing_model,
      base: pick(a.override_base_monthly_price, t.base_monthly_price),
      perUnit: pick(a.override_per_user_price, t.per_user_price),
      setup: pick(a.override_setup_fee, t.setup_fee),
      reimb: pick(a.override_revenue_share_config?.reimbursement_share_percent, t.revenue_share_config?.reimbursement_share_percent),
    };
  }, [data]);

  const feeByType = useMemo(() => {
    const m: Record<string, any> = {};
    (data?.fees || []).forEach((f: any) => (m[f.service_type] = f));
    return m;
  }, [data]);
  const feeStr = (f: any) => {
    if (f.billing_model === 'invoiced') return 'billed by invoice';
    const parts: string[] = [];
    if (f.fee_type !== 'flat') parts.push(`${(f.fee_percent * 100).toFixed(2)}%`);
    if (f.fee_type !== 'percent' && f.fee_fixed_cents > 0) parts.push(`$${(f.fee_fixed_cents / 100).toFixed(2)}`);
    return parts.join(' + ') || 'no fee';
  };
  const FEE_LABELS: Record<string, string> = {
    donation_txn: 'Donations & grants',
    disbursement: 'Card disbursements',
    card_program: 'Card program',
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <div className="mb-3 flex items-center gap-2">
        <Package className="h-5 w-5 text-indigo-600" />
        <h3 className="text-lg font-semibold text-gray-900">Organization Billing Profile</h3>
      </div>
      <p className="mb-4 text-sm text-gray-500">
        The complete pricing package for one org — plan, fees, and per-org consumption/AI rates in one place.
        Edit each part in the sections below.
      </p>

      <label className="block max-w-sm">
        <span className="text-sm font-medium text-gray-700">Organization</span>
        <select className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          value={cboId ?? ''} onChange={(e) => { const v = e.target.value ? Number(e.target.value) : undefined; setCboId(v); if (v) load(v); }}>
          <option value="">Select an organization…</option>
          {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      </label>

      {loading && <div className="mt-4 flex items-center text-sm text-gray-500"><Loader2 className="mr-2 h-4 w-4 animate-spin" />Loading profile…</div>}

      {data && !loading && (
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
          {/* Plan */}
          <div className="rounded-md border border-gray-100 p-4">
            <div className="mb-2 flex items-center gap-2 text-indigo-700"><Building2 className="h-4 w-4" /><span className="font-medium">Plan</span></div>
            {eff ? (
              <>
                <div className="mb-1 text-sm font-semibold text-gray-900">{eff.name}</div>
                <div className="mb-2 text-xs text-gray-400">{modelLabel[eff.model] || eff.model}</div>
                {['flat_fee', 'per_seat', 'pmpm', 'hybrid'].includes(eff.model) && <Val label="Base" value={`${money(eff.base.v)}/mo`} custom={eff.base.custom} />}
                {['per_seat', 'pmpm', 'hybrid'].includes(eff.model) && <Val label={eff.model === 'pmpm' ? 'Per member' : 'Per user'} value={`${money(eff.perUnit.v)}/mo`} custom={eff.perUnit.custom} />}
                {['revenue_share', 'hybrid'].includes(eff.model) && <Val label="Reimbursement" value={`${eff.reimb.v || 0}%`} custom={eff.reimb.custom} />}
                {Number(eff.setup.v) > 0 && <Val label="Setup fee" value={money(eff.setup.v)} custom={eff.setup.custom} />}
              </>
            ) : (
              <p className="text-sm text-gray-400">No plan assigned. Assign one below.</p>
            )}
          </div>

          {/* Transaction fees */}
          <div className="rounded-md border border-gray-100 p-4">
            <div className="mb-2 flex items-center gap-2 text-emerald-700"><Percent className="h-4 w-4" /><span className="font-medium">Transaction fees</span></div>
            {(data.fees || []).length === 0 ? (
              <p className="text-sm text-gray-400">No transaction fees assigned.</p>
            ) : (
              (data.fees || []).map((f: any) => (
                <Val key={f.service_type} label={FEE_LABELS[f.service_type] || f.service_type} value={feeStr(f)} custom />
              ))
            )}
          </div>

          {/* Consumption + AI */}
          <div className="rounded-md border border-gray-100 p-4">
            <div className="mb-2 flex items-center gap-2 text-blue-700"><MessageSquare className="h-4 w-4" /><span className="font-medium">Consumption &amp; AI</span></div>
            {data.comm.length === 0 && data.ai.length === 0 ? (
              <p className="text-sm text-gray-400">No consumption pricing assigned.</p>
            ) : (
              <>
                {data.comm.map((r: any) => <Val key={r.id} label={r.channel_display_name || r.channel} value={`${money(r.cost_per_unit)}/${r.unit_name || 'unit'}`} custom />)}
                {data.ai.map((r: any) => (
                  <Val key={r.id} label={`AI (${r.model_name || r.service_name || 'tokens'})`}
                    value={`${money(r.input_rate_per_million)} in / ${money(r.output_rate_per_million)} out /1M`} custom />
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
